/**
 * ProjectView - Shows project details and list of areas
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { formatCurrency } from '../utils/calculations';

const ProjectView = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  
  const [project, setProject] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    let cancelled = false;
    
    const loadData = async () => {
      try {
        setLoading(true);
        const [projectData, areasData] = await Promise.all([
          api.getProject(projectId),
          api.getAreas(projectId)
        ]);
        if (!cancelled) {
          setProject(projectData.project);
          setAreas(areasData);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleDeleteArea = async (areaId, areaName) => {
    if (!confirm(`Delete area "${areaName}"? This cannot be undone.`)) return;
    
    try {
      await api.deleteArea(projectId, areaId);
      setAreas(areas.filter(a => a.id !== areaId));
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const handleCloneArea = async (areaId, areaName) => {
    try {
      const cloned = await api.cloneArea(projectId, areaId, `${areaName} (Copy)`);
      setAreas([...areas, cloned]);
    } catch (err) {
      alert('Failed to clone: ' + err.message);
    }
  };

  const startRename = (area) => {
    setRenaming(area.id);
    setNewName(area.name);
  };

  const handleRename = async (areaId) => {
    if (!newName.trim()) {
      setRenaming(null);
      return;
    }
    
    try {
      await api.renameArea(projectId, areaId, newName.trim());
      setAreas(areas.map(a => a.id === areaId ? { ...a, name: newName.trim() } : a));
      setRenaming(null);
    } catch (err) {
      alert('Failed to rename: ' + err.message);
    }
  };

  const totalValue = areas.reduce((sum, a) => sum + (parseFloat(a.grand_total) || 0), 0);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
        <p className="text-slate-400 mt-4">Loading project...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500 rounded-lg p-6 text-center">
        <p className="text-red-400">{error}</p>
        <button onClick={() => navigate('/')} className="mt-4 text-amber-500 hover:text-amber-400">
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <Link to="/" className="text-slate-400 hover:text-white text-sm flex items-center gap-2 mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All Projects
        </Link>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-white">{project?.name || 'Untitled Project'}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
              {project?.client_name && <span>{project.client_name}</span>}
              {project?.reference && <span>Ref: {project.reference}</span>}
            </div>
            {project?.address_line1 && (
              <p className="text-slate-500 text-sm mt-1">
                {[project.address_line1, project.city, project.postcode].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-slate-400 text-sm">Total Value</p>
            <p className="text-2xl font-bold text-amber-500">{formatCurrency(totalValue)}</p>
          </div>
        </div>
      </div>

      {/* Areas Section */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700 flex justify-between items-center">
          <h2 className="font-semibold text-white">
            Areas ({areas.length})
          </h2>
          <Link
            to={`/project/${projectId}/area/new`}
            className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium px-4 py-1.5 rounded-lg transition-colors text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Area
          </Link>
        </div>

        {areas.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No areas yet</h3>
            <p className="text-slate-400 mb-6">Add your first roof area to start estimating</p>
            <Link
              to={`/project/${projectId}/area/new`}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium px-6 py-2 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Area
            </Link>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-slate-400 text-sm border-b border-slate-700 bg-slate-900/50">
                <th className="text-left py-3 px-4 font-medium">Area</th>
                <th className="text-left py-3 px-4 font-medium">Spec</th>
                <th className="text-right py-3 px-4 font-medium">Area (m²)</th>
                <th className="text-right py-3 px-4 font-medium">Materials</th>
                <th className="text-right py-3 px-4 font-medium">Labour</th>
                <th className="text-right py-3 px-4 font-medium">Total</th>
                <th className="text-center py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {areas.map(area => (
                <tr key={area.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                  <td className="py-3 px-4">
                    {renaming === area.id ? (
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onBlur={() => handleRename(area.id)}
                        onKeyDown={(e) => e.key === 'Enter' && handleRename(area.id)}
                        className="bg-slate-700 border border-amber-500 rounded px-2 py-1 text-white text-sm w-full"
                        autoFocus
                      />
                    ) : (
                      <Link 
                        to={`/project/${projectId}/area/${area.id}`}
                        className="text-white font-medium hover:text-amber-500 transition-colors"
                      >
                        {area.name}
                      </Link>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-400 text-sm">
                    {area.spec_summary || '—'}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-300">
                    {area.total_area_m2 ? `${parseFloat(area.total_area_m2).toFixed(2)}` : '—'}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-300">
                    {formatCurrency(area.materials_total)}
                  </td>
                  <td className="py-3 px-4 text-right text-slate-300">
                    {formatCurrency(area.labour_total)}
                  </td>
                  <td className="py-3 px-4 text-right text-amber-500 font-medium">
                    {formatCurrency(area.grand_total)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-1">
                      <Link
                        to={`/project/${projectId}/area/${area.id}`}
                        className="text-slate-400 hover:text-white p-1 transition-colors"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => startRename(area)}
                        className="text-slate-400 hover:text-white p-1 transition-colors"
                        title="Rename"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleCloneArea(area.id, area.name)}
                        className="text-slate-400 hover:text-white p-1 transition-colors"
                        title="Clone"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteArea(area.id, area.name)}
                        className="text-slate-400 hover:text-red-400 p-1 transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProjectView;
